// Agent portal pages: dashboard (dialing), current call with disposition, history, callbacks, profile.
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone, SkipForward, Ban, CalendarClock, UserRound } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { useAgentMe, useAgentState } from '@/hooks/agent';
import { usePhone } from '@/store/phone';
import { useLiveRefresh } from '@/hooks/live';
import { toast } from '@/store/toast';
import { PageHeader, DataTable, Pager, ErrorBox, Field, Stat, Loading } from '@/components/common';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea, Checkbox } from '@/components/ui/input';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { fmtTime, fmtDuration, pct, enumLabel } from '@/lib/utils';
import { t } from '@/i18n';
import { CrmPanel } from '@/components/CrmPanel';

// ---- Dashboard -------------------------------------------------------------------------------
export function AgentDashboard() {
  const me = useAgentMe();
  const st = useAgentState();
  const stats = useQuery({ queryKey: ['agent-stats'], queryFn: () => api('/agent/stats'), refetchInterval: 30000 });
  const s = Array.isArray(stats.data?.agents) ? stats.data.agents[0] ?? {} : {};
  const state = st.data?.agent.state;
  return (
    <div className="grid gap-4">
      <PageHeader title={t('Dashboard')} description={t('Your numbers today and your dialing tools.')} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label={t('Calls')} value={s.calls_handled ?? 0} />
        <Stat label={t('Talk time')} value={fmtDuration(s.talk_sec)} />
        <Stat label={t('Logged in')} value={fmtDuration(s.logged_in_sec)} />
        <Stat label={t('Utilization')} value={pct(s.utilization ?? 0)} />
      </div>
      {state === 'LOGGED_OUT' && <Card><CardContent className="pt-4 text-sm">{t('You are logged out. Use')} <b>{t('Log in')}</b> {t('at the top to start your shift.')}</CardContent></Card>}
      <div className="grid gap-4 xl:grid-cols-2">
        <ManualDial campaigns={me.data?.campaigns ?? []} disabled={state !== 'READY' && state !== 'PAUSED'} />
        <PreviewPanel campaigns={(me.data?.campaigns ?? []).filter((c) => c.dial_method === 'PREVIEW')} disabled={state !== 'READY' && state !== 'PAUSED'} />
      </div>
      <Card>
        <CardHeader><CardTitle>{t('Your campaigns and queues')}</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2 text-sm">
          {(me.data?.campaigns ?? []).map((c) => <Badge key={`c${c.id}`} variant="outline">{c.name} · {enumLabel(c.dial_method).toLowerCase()} · {enumLabel(c.status).toLowerCase()}</Badge>)}
          {(me.data?.queues ?? []).map((q) => <Badge key={`q${q.id}`} variant="info">{t('queue {{name}}', { name: q.name })}</Badge>)}
          {me.data && !me.data.campaigns.length && !me.data.queues.length && <span className="text-muted-foreground">{t('You are not in any campaign or queue yet.')}</span>}
        </CardContent>
      </Card>
    </div>
  );
}

function ManualDial({ campaigns, disabled }: { campaigns: { id: number; name: string; dial_method: string }[]; disabled: boolean }) {
  const phone = usePhone();
  const [number, setNumber] = React.useState('');
  const [campaign, setCampaign] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  async function dial(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    phone.expectCall();                               // the engine rings this phone first
    try { await api('/agent/dial', { body: { number, campaign_id: campaign ? Number(campaign) : undefined } }); toast.ok(t('Calling {{number}}', { number })); }
    catch (err) { setError(err); phone.expectCall(0); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>{t('Manual dial')}</CardTitle><CardDescription>{t('The engine rings your softphone, then dials the customer. DNC and calling hours are checked first.')}</CardDescription></CardHeader>
      <CardContent>
        <form onSubmit={dial} className="grid gap-3">
          <Field help="manualdial.number" label={t('Number')}><Input name="number" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="15551234567" required /></Field>
          <Field help="manualdial.campaign" label={t('Campaign')}><Select value={campaign} onChange={(e) => setCampaign(e.target.value)}>
            <option value="">{t('No campaign')}</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select></Field>
          <ErrorBox error={error} />
          <Button type="submit" disabled={disabled || phone.reg !== 'registered'}><Phone /> {t('Dial')}</Button>
          {phone.reg !== 'registered' && <span className="text-xs text-muted-foreground">{t('Your softphone must be registered to dial.')}</span>}
        </form>
      </CardContent>
    </Card>
  );
}

function PreviewPanel({ campaigns, disabled }: { campaigns: { id: number; name: string }[]; disabled: boolean }) {
  const phone = usePhone();
  const qc = useQueryClient();
  const [campaign, setCampaign] = React.useState('');
  const [cbOpen, setCbOpen] = React.useState(false);
  const preview = useQuery({ queryKey: ['agent-preview'], queryFn: () => api('/agent/preview').catch(() => null), refetchInterval: false });
  const contact = preview.data?.contact;
  const refresh = () => qc.invalidateQueries({ queryKey: ['agent-preview'] });
  // the reserved contact changed in the engine (expired, dialed, released)
  useLiveRefresh(['contact.updated'], () => { refresh(); });
  useLiveRefresh(['call.hangup'], () => qc.invalidateQueries({ queryKey: ['agent-stats'] }));
  async function run(path: string, body?: unknown, expect = false) {
    if (expect) phone.expectCall();
    try { const r = await api(`/agent/preview/${path}`, { body: body ?? {} }); refresh(); return r; }
    catch (e) { toast.error(errorText(e)); if (expect) phone.expectCall(0); }
  }
  if (!campaigns.length) return null;
  return (
    <Card>
      <CardHeader><CardTitle>{t('Preview dialing')}</CardTitle><CardDescription>{t('See the next contact before you call.')}</CardDescription></CardHeader>
      <CardContent className="grid gap-3">
        {!contact ? (
          <div className="flex gap-2">
            <Select value={campaign} onChange={(e) => setCampaign(e.target.value)} aria-label={t('Preview campaign')}>
              <option value="">{t('Choose a campaign…')}</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Button disabled={disabled || !campaign} onClick={() => run('next', { campaign_id: Number(campaign) })}>{t('Next contact')}</Button>
          </div>
        ) : (<>
          <ContactCard c={contact} />
          <div className="flex flex-wrap gap-2">
            <Button variant="success" disabled={phone.reg !== 'registered'} onClick={() => run('call', {}, true)}><Phone /> {t('Call')}</Button>
            <Button variant="outline" onClick={() => run('skip')}><SkipForward /> {t('Skip')}</Button>
            <Button variant="outline" onClick={() => setCbOpen(true)}><CalendarClock /> {t('Callback')}</Button>
            <Button variant="outline" onClick={() => confirm(t('Mark this number Do Not Call?')) && run('dnc', { reason: 'agent' })}><Ban /> {t('DNC')}</Button>
          </div>
        </>)}
        <Dialog open={cbOpen} onOpenChange={setCbOpen}>
          {cbOpen && <DialogContent title={t('Schedule a callback')}><CallbackWhen onSubmit={async (inSec, anyone, notes) => { await run('callback', { in_sec: inSec, anyone, notes }); setCbOpen(false); }} /></DialogContent>}
        </Dialog>
      </CardContent>
    </Card>
  );
}

function ContactCard({ c }: { c: Record<string, any> }) {
  const custom = c.custom_fields && typeof c.custom_fields === 'object' ? Object.entries(c.custom_fields) : [];
  return (
    <div className="grid gap-1 rounded-lg border p-3 text-sm" data-testid="contact-card">
      <div className="flex items-center gap-2 text-base font-medium"><UserRound className="h-4 w-4" /> {[c.first_name, c.last_name].filter(Boolean).join(' ') || t('Unknown name')}</div>
      <div className="tabular-nums">{c.phone_number}</div>
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {c.status && <StatusBadge value={c.status} />}{c.email && <span>{c.email}</span>}{c.timezone && <span>{c.timezone}</span>}
        {c.attempt_count !== undefined && <span>{t('attempts {{n}}', { n: c.attempt_count })}</span>}{c.last_result && <span>{t('last {{result}}', { result: enumLabel(c.last_result) })}</span>}
      </div>
      {custom.length > 0 && <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs">{custom.map(([k, v]) => <div key={k}><span className="text-muted-foreground">{k}:</span> {String(v)}</div>)}</div>}
    </div>
  );
}

function CallbackWhen({ onSubmit }: { onSubmit: (inSec: number, anyone: boolean, notes: string) => Promise<void> }) {
  const [when, setWhen] = React.useState('');
  const [anyone, setAnyone] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  return (
    <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); onSubmit(when ? Math.max(0, Math.round((new Date(when).getTime() - Date.now()) / 1000)) : 3600, anyone, notes); }}>
      <Field help="previewcallback.when" label={t('When')} hint={t('Your local time. Empty means in one hour.')}><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={anyone} onChange={(e) => setAnyone(e.target.checked)} /> {t('Any agent may take it')}</label>
      <Field help="previewcallback.notes" label={t('Notes')}><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      <div className="flex justify-end"><Button type="submit">{t('Schedule')}</Button></div>
    </form>
  );
}

// ---- Current call + disposition -----------------------------------------------------------------
export function AgentCall() {
  const qc = useQueryClient();
  const st = useAgentState();
  const phone = usePhone();
  const call = useQuery({ queryKey: ['agent-call', st.data?.agent.state, st.data?.agent.call_uuid], queryFn: () => api('/agent/call'), refetchInterval: 5000 });
  const [code, setCode] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [cbIn, setCbIn] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  const d = call.data;
  const me = useAgentMe();
  // the CRM panel stays on the last call through wrap-up, until the next call comes
  const lastUuid = React.useRef<string | null>(null);
  if (d?.call?.call_uuid) lastUuid.current = d.call.call_uuid;
  const crmCall = d?.call?.call_uuid ?? st.data?.agent.call_uuid ?? lastUuid.current;
  const dispos: any[] = d?.dispositions ?? [];
  const chosen = dispos.find((x) => x.code === code);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try {
      await api('/agent/disposition', { body: { code, notes: notes || undefined } });
      if (chosen?.creates_callback && cbIn && d?.contact?.id) {
        // the engine's disposition creates the callback with the campaign default delay; a chosen time reschedules it
        toast.ok(t('Saved; adjust the callback time on the Callbacks page if needed'));
      } else toast.ok(t('Disposition saved'));
      setCode(''); setNotes(''); setCbIn('');
      qc.invalidateQueries({ queryKey: ['agent-state'] });
      qc.invalidateQueries({ queryKey: ['agent-call'] });
    } catch (err) { setError(err); }
  }

  if (call.isLoading) return <Loading />;
  return (
    <div className="grid gap-4">
      <PageHeader title={t('Call')} description={t('The call you are on, or the one you are wrapping up.')} />
      <ErrorBox error={call.error} />
      {!d?.call && phone.call === 'idle' && <Card><CardContent className="pt-4 text-sm text-muted-foreground">{t('No call right now.')}</CardContent></Card>}
      {d?.call && (
        <Card>
          <CardHeader className="flex-row items-center justify-between"><CardTitle>{d.call.direction === 'INBOUND' ? t('Inbound call') : t('Outbound call')}</CardTitle><StatusBadge value={d.call.status} /></CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-2">
            <div className="grid gap-1">
              <div><span className="text-muted-foreground">{t('From')}</span> {d.call.source}</div>
              <div><span className="text-muted-foreground">{t('To')}</span> {d.call.destination}</div>
              {d.call.campaign_name && <div><span className="text-muted-foreground">{t('Campaign')}</span> {d.call.campaign_name}</div>}
              {d.call.queue_name && <div><span className="text-muted-foreground">{t('Queue')}</span> {d.call.queue_name}</div>}
              <div><span className="text-muted-foreground">{t('Started')}</span> {fmtTime(d.call.created_at)}</div>
            </div>
            {d.contact && <ContactCard c={d.contact} />}
          </CardContent>
        </Card>
      )}
      <CrmPanel callUuid={crmCall} campaigns={me.data?.campaigns ?? []} />
      {(d?.disposition_required || st.data?.agent.state === 'WRAPUP') && (
        <Card className="border-warning" data-testid="disposition-form">
          <CardHeader><CardTitle>{t('Disposition required')}</CardTitle><CardDescription>{t('Pick the outcome of this call to finish wrap-up.')}</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-3">
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('Disposition')}>
                {dispos.map((x) => (
                  <Button key={x.id} type="button" role="radio" aria-checked={code === x.code} variant={code === x.code ? 'default' : 'outline'} size="sm" onClick={() => setCode(x.code)}>
                    {x.code} · {x.name}
                  </Button>
                ))}
              </div>
              {chosen?.creates_callback ? <Field help="disposition.callback_in" label={t('Call back in (minutes)')}><Input type="number" min={1} value={cbIn} onChange={(e) => setCbIn(e.target.value)} /></Field> : null}
              <Field help="disposition.notes" label={t('Notes')}><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></Field>
              <ErrorBox error={error} />
              <Button type="submit" disabled={!code}>{t('Save disposition')}</Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---- History ------------------------------------------------------------------------------------
export function AgentHistory() {
  const [offset, setOffset] = React.useState(0);
  const h = useQuery({ queryKey: ['agent-history', offset], queryFn: () => api<{ rows: any[] }>('/agent/history', { query: { limit: 25, offset } }) });
  return (
    <div>
      <PageHeader title={t('History')} description={t('Calls you took part in.')} />
      <ErrorBox error={h.error} />
      <DataTable rowKey={(r) => r.call_uuid} rows={h.data?.rows} empty={t('No calls yet.')} columns={[
        { key: 'start_time', label: t('When'), render: (r) => fmtTime(r.start_time) }, { key: 'direction', label: t('Dir'), render: (r) => enumLabel(r.direction) },
        { key: 'source', label: t('From') }, { key: 'destination', label: t('To') }, { key: 'campaign_name', label: t('Campaign') },
        { key: 'talk_sec', label: t('Talk'), render: (r) => r.talk_sec == null ? '-' : fmtDuration(r.talk_sec) },
        { key: 'hangup_cause', label: t('End') }, { key: 'disposition', label: t('Disposition') }]} />
      <Pager offset={offset} limit={25} total={h.data && h.data.rows.length < 25 ? offset + h.data.rows.length : undefined} onChange={setOffset} />
    </div>
  );
}

// ---- Callbacks ----------------------------------------------------------------------------------
export function AgentCallbacks() {
  const [status, setStatus] = React.useState('OPEN');
  const cb = useQuery({ queryKey: ['agent-callbacks', status], queryFn: () => api<{ rows: any[] }>('/agent/callbacks', { query: { status } }), refetchInterval: 15000 });
  const [resched, setResched] = React.useState<any | null>(null);
  async function cancel(r: any) {
    if (!confirm(t('Cancel this callback?'))) return;
    try { await api(`/agent/callbacks/${r.id}/cancel`, { body: {} }); toast.ok(t('Cancelled')); cb.refetch(); } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div>
      <PageHeader title={t('Callbacks')} description={t('Customers waiting for you to call back. Due callbacks are dialed through your softphone.')}>
        <Select className="w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="OPEN">{t('Open')}</option>{['COMPLETED', 'MISSED', 'CANCELLED', 'EXPIRED'].map((v) => <option key={v} value={v}>{enumLabel(v)}</option>)}
        </Select>
      </PageHeader>
      <ErrorBox error={cb.error} />
      <DataTable rows={cb.data?.rows} empty={t('No callbacks.')} columns={[
        { key: 'callback_time', label: t('When'), render: (r) => fmtTime(r.callback_time) }, { key: 'phone_number', label: t('Number') },
        { key: 'first_name', label: t('Name'), render: (r) => `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || '-' },
        { key: 'campaign_name', label: t('Campaign') }, { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> },
        { key: 'notes', label: t('Notes') }]}
        actions={(r) => ['PENDING', 'DUE'].includes(r.status) ? (<>
          <Button size="sm" variant="ghost" onClick={() => setResched(r)}>{t('Reschedule')}</Button>
          <Button size="sm" variant="ghost" onClick={() => cancel(r)}>{t('Cancel')}</Button>
        </>) : null} />
      <QueueCallbacks />
      <Dialog open={!!resched} onOpenChange={(o) => !o && setResched(null)}>
        {resched && <DialogContent title={t('Reschedule callback')}>
          <CallbackWhen onSubmit={async (inSec) => {
            try { await api(`/agent/callbacks/${resched.id}/reschedule`, { body: { in_sec: inSec } }); toast.ok(t('Rescheduled')); setResched(null); cb.refetch(); }
            catch (e) { toast.error(errorText(e)); }
          }} />
        </DialogContent>}
      </Dialog>
    </div>
  );
}

// callers of my queues who asked to be called back. MANUAL / PREVIEW campaigns: press Call (the
// customer is dialed through the softphone, agent first); the others are dialed by the engine.
function QueueCallbacks() {
  const cb = useQuery({ queryKey: ['agent-queue-callbacks'], queryFn: () => api<{ rows: any[] }>('/agent/queue-callbacks'), refetchInterval: 5000 });
  const [busy, setBusy] = React.useState<number | null>(null);
  async function call(r: any) {
    setBusy(r.id);
    try { await api(`/agent/queue-callbacks/${r.id}/call`, { body: {} }); toast.ok(t('Calling {{number}}', { number: r.phone_number })); cb.refetch(); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(null); }
  }
  return (
    <div className="mt-8">
      <h2 className="mb-2 text-lg font-semibold">{t('Queue callbacks')}</h2>
      <p className="mb-3 text-sm text-muted-foreground">{t('Callers of your queues who pressed the callback key. Manual and preview campaigns wait for you to press Call; progressive and predictive ones are dialed for you.')}</p>
      <ErrorBox error={cb.error} />
      <DataTable rows={cb.data?.rows} empty={t('No queue callbacks.')} columns={[
        { key: 'callback_time', label: t('When'), render: (r) => fmtTime(r.callback_time) }, { key: 'phone_number', label: t('Number') },
        { key: 'queue_name', label: t('Queue') }, { key: 'campaign_name', label: t('Campaign') },
        { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> },
        { key: 'manual', label: t('Dialing'), render: (r) => Number(r.manual) ? t('you') : t('automatic') }]}
        actions={(r) => Number(r.manual) && r.status === 'DUE'
          ? <Button size="sm" onClick={() => call(r)} disabled={busy === r.id}><Phone /> {t('Call')}</Button> : null} />
    </div>
  );
}

// ---- Profile ------------------------------------------------------------------------------------
export function AgentProfile() {
  const me = useAgentMe();
  const phone = usePhone();
  const [cur, setCur] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  async function change(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try { await api('/auth/password', { body: { current: cur, password: pw } }); toast.ok(t('Password changed')); setCur(''); setPw(''); } catch (err) { setError(err); }
  }
  const a = me.data?.agent;
  return (
    <div className="grid gap-4">
      <PageHeader title={t('Profile')} />
      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>{t('Agent')}</CardTitle></CardHeader>
          <CardContent className="grid gap-1 text-sm">
            <div><span className="text-muted-foreground">{t('Name')}</span> {a?.name}</div>
            <div><span className="text-muted-foreground">{t('Agent code')}</span> {a?.code}</div>
            <div><span className="text-muted-foreground">{t('SIP address')}</span> {me.data?.sip.uri}</div>
            <div><span className="text-muted-foreground">{t('Server')}</span> {me.data?.sip.wss_url}</div>
            <div><span className="text-muted-foreground">{t('Softphone')}</span> {t(phone.reg)}</div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => me.data && phone.connect(me.data.sip)}>{t('Reconnect softphone')}</Button>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{t('Change password')}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={change} className="grid gap-3">
              <Field help="profile.current_password" label={t('Current password')}><Input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} required /></Field>
              <Field help="profile.new_password" label={t('New password')} hint={t('At least 8 characters.')}><Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required /></Field>
              <ErrorBox error={error} />
              <Button type="submit">{t('Change')}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle>{t('Softphone log')}</CardTitle></CardHeader>
        <CardContent><pre className="max-h-64 overflow-auto text-xs" data-testid="phone-log">{phone.log.join('\n') || t('Nothing yet.')}</pre></CardContent>
      </Card>
    </div>
  );
}
